import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Combobox } from '../../components/ui/Combobox';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { DateRangePicker } from '../reporting/DateRangePicker';
import { PanelTitle } from '../reporting/PanelTitle';
import { presetToRange } from '../../lib/reportingDateRange';
import type { ReportingDatePreset } from '../../types/reporting';
import { buildLeadBotReport, includeToday, type CountByKey } from '../../lib/leadBot/reporting';
import { conversationHref } from '../../lib/leadBot/queueView';
import {
  UNMEASURED,
  formatCount,
  formatDecimal,
  formatDuration,
  formatTokens,
  rateCaption,
  rateHeadline,
} from '../../lib/leadBot/reportFormat';
import { LEAD_OUTCOME_LABELS } from '../../types/leadOutcome.js';
import { getReportData } from './api';
import { ReportStatCard } from './ReportStatCard';

// SMS Lead Bot — the first reporting dashboard.
//
// ⚠️⚠️ TIER 1 LEADS, AND THAT IS THE TRIAL QUESTION. "Is this safe to point at a
// real customer" comes before "is it working" and long before "did it book
// anyone" — so compliance triggers are the first thing on the page, as a LIST
// linking to transcripts rather than a count. A number alone tells you nothing
// you can act on; the value in a trial is reading what the bot actually said.
//
// ⚠️ EVERY HONESTY RULE LIVES UPSTREAM, in the pure `reporting.ts` and
// `reportFormat.ts`. This file renders strings that were already decided. It
// must never reach for `?? 0` — that single character is how "unmeasured is a
// dash" becomes "the bot booked nobody".
//
// ⚠️ FILTERS ARE CLIENT-SIDE over a fetch-all, so changing one costs no round
// trip and every panel narrows together (the aggregation takes one filtered set
// — see the whole-page-invariant test).

function CountList({
  rows,
  empty,
}: {
  rows: CountByKey[];
  empty: string;
}) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return (
    <ul className="space-y-1.5">
      {rows.map((row) => (
        <li key={row.key} className="flex items-center gap-2 text-sm">
          <span className="w-10 shrink-0 text-right font-medium tabular-nums">{row.count}</span>
          <span className="min-w-0 flex-1 truncate capitalize">{row.label}</span>
          <span
            aria-hidden
            className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-slate-100"
          >
            <span
              className="block h-full rounded-full bg-kyck-500"
              style={{ width: `${total > 0 ? (row.count / total) * 100 : 0}%` }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <PanelTitle>{title}</PanelTitle>
      <div className="mt-3">{children}</div>
    </div>
  );
}

/** A plain bar row set — used where the x-axis is a turn number or an hour,
 *  neither of which is a date series the shared trend chart understands. */
function BarRows({ rows }: { rows: Array<{ label: string; value: number; hint?: string }> }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-1">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 text-xs" title={row.hint}>
          <span className="w-12 shrink-0 text-right text-muted-foreground">{row.label}</span>
          <span className="h-3 flex-1 overflow-hidden rounded bg-slate-100">
            <span
              className="block h-full rounded bg-kyck-500"
              style={{ width: `${(row.value / max) * 100}%` }}
            />
          </span>
          <span className="w-8 shrink-0 tabular-nums">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

export function LeadBotReportPage() {
  const [preset, setPreset] = useState<ReportingDatePreset>('past_30');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [accountId, setAccountId] = useState('');
  const [botConfigId, setBotConfigId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['lead-bot', 'report-data'],
    queryFn: getReportData,
  });

  // ⚠️ The SHARED presetToRange — a fourth definition of "last month" is how
  // surfaces start quietly disagreeing about what a period is. ⚠️ Extended to
  // include TODAY: the shared rolling windows end yesterday because ad-platform
  // data for today is incomplete, which is not true of our own conversation
  // records. See includeToday for why that matters to the in-flight rule.
  const range = useMemo(() => {
    const now = new Date();
    return includeToday(
      presetToRange(preset, now, custom.from && custom.to ? custom : undefined),
      preset,
      now,
    );
  }, [preset, custom]);

  const accountOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const config of data?.configs ?? []) {
      if (config.accountId) seen.set(config.accountId, config.accountName ?? config.accountId);
    }
    return [
      { value: '', label: 'All clients' },
      ...[...seen.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [data?.configs]);

  const botOptions = useMemo(() => {
    const configs = (data?.configs ?? []).filter(
      (c) => accountId === '' || c.accountId === accountId,
    );
    return [
      { value: '', label: 'All bots' },
      ...configs.map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [data?.configs, accountId]);

  const report = useMemo(
    () =>
      buildLeadBotReport({
        conversations: data?.conversations ?? [],
        messages: data?.messages ?? [],
        filters: {
          accountId: accountId || null,
          botConfigId: botConfigId || null,
          from: range.from,
          to: range.to,
        },
        nowMs: Date.now(),
      }),
    [data, accountId, botConfigId, range],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lead bot report"
        description="How the SMS assistant is behaving, and what it is producing."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="report-client">
            Client
          </label>
          <Combobox
            id="report-client"
            value={accountId}
            onValueChange={(v) => {
              setAccountId(v);
              // A bot from another client would filter everything to nothing.
              setBotConfigId('');
            }}
            options={accountOptions}
            placeholder="All clients"
          />
        </div>
        <div className="w-48">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="report-bot">
            Bot
          </label>
          <SelectField
            id="report-bot"
            value={botConfigId}
            onValueChange={setBotConfigId}
            options={botOptions}
          />
        </div>
        <DateRangePicker
          preset={preset}
          onPresetChange={setPreset}
          custom={custom}
          onCustomChange={setCustom}
        />
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {/* ------------------------------------------------ tier 1 ------ */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">Is it safe?</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <ReportStatCard
                label="Compliance triggers"
                value={formatCount(report.complianceHits.length)}
                caption="licence, towing or weight — always deflected"
                tone="alert"
              />
              <ReportStatCard
                label="Handoffs"
                value={formatCount(report.handoffCount)}
                caption={`of ${report.conversationsStarted} conversations`}
              />
              <ReportStatCard
                label="Sends blocked"
                value={formatCount(
                  report.blocksByReason.reduce((sum, row) => sum + row.count, 0),
                )}
                caption="a cap or a STOP stopped these"
                tone="alert"
              />
              <ReportStatCard
                label="Send failures"
                value={formatCount(report.sendFailures)}
                caption="the provider rejected these"
                tone="alert"
              />
            </div>

            {/* ⚠️ THE HEADLINE OF THE PAGE, and a LIST rather than a tile. The
                whole value in a trial is reading what the bot actually said, so
                every hit opens its transcript. */}
            <Panel title="Compliance triggers — read every one">
              {report.complianceHits.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  None in this period. The detector runs on every inbound message, so this is a
                  measured zero rather than an absence of data.
                </p>
              ) : (
                <ul className="divide-y">
                  {report.complianceHits.map((hit) => (
                    <li key={`${hit.conversationId}-${hit.detail}`} className="py-2">
                      <Link
                        to={conversationHref(hit.conversationId)}
                        className="flex items-start gap-2 text-sm hover:underline"
                      >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium">{hit.leadName}</span>
                          {hit.accountName && (
                            <span className="text-muted-foreground"> · {hit.accountName}</span>
                          )}
                          <span className="block text-muted-foreground">{hit.detail}</span>
                        </span>
                        <Badge variant="neutral">{hit.source}</Badge>
                        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel title="Handoffs by reason">
                {/* ⚠️ The MIX is the signal: whether the bot is deflecting things
                    it should handle, or handling things it should deflect. */}
                <CountList rows={report.handoffsByReason} empty="No handoffs in this period." />
              </Panel>
              <Panel title="Blocked sends by reason">
                <CountList
                  rows={report.blocksByReason}
                  empty="Nothing was blocked in this period."
                />
              </Panel>
            </div>

            {/* ⚠️ The honest framing of what the trial IS. Currently every send. */}
            <p className="text-xs text-muted-foreground">
              {formatCount(report.unreviewedSends)} of {formatCount(report.totalSends)} messages
              went out without a person reading them first.
            </p>
          </section>

          {/* ------------------------------------------------ tier 2 ------ */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">Is it working?</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <ReportStatCard
                label="Conversations started"
                value={formatCount(report.conversationsStarted)}
              />
              <ReportStatCard
                label="Reply rate"
                value={rateHeadline(report.replyRate)}
                // ⚠️ The caption carries the denominator AND says when the
                // sample was too small for a percentage.
                caption={
                  report.inFlightExcluded > 0
                    ? `${rateCaption(report.replyRate)} · ${report.inFlightExcluded} still in flight, excluded`
                    : rateCaption(report.replyRate)
                }
              />
              <ReportStatCard
                label="Median turns"
                value={formatDecimal(report.medianTurns, 0)}
                caption="messages each way, per conversation"
              />
              <ReportStatCard
                label="Median time to our reply"
                value={formatDuration(report.medianFirstReplySeconds)}
                caption="from the lead's message to ours"
              />
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel title="Where conversations stop">
                {report.dropOff.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing measured yet.</p>
                ) : (
                  <>
                    <BarRows
                      rows={report.dropOff.map((point) => ({
                        label: `Turn ${point.turn}`,
                        value: point.reached,
                        hint: `${point.reached} reached this exchange · ${point.stopped} stopped here`,
                      }))}
                    />
                    <p className="mt-2 text-xs text-muted-foreground">
                      How many conversations reached each exchange. Hover for how many stopped
                      there.
                    </p>
                  </>
                )}
              </Panel>
              <Panel title="When leads message us">
                {report.inboundByHour.every((n) => n === 0) ? (
                  <p className="text-sm text-muted-foreground">
                    No inbound messages in this period.
                  </p>
                ) : (
                  <>
                    <BarRows
                      rows={report.inboundByHour
                        .map((count, hour) => ({ label: `${String(hour).padStart(2, '0')}:00`, value: count }))
                        .filter((row) => row.value > 0)}
                    />
                    {/* The 24/7 argument, evidenced rather than asserted. */}
                    <p className="mt-2 text-xs text-muted-foreground">
                      Inbound messages by hour, in your local time.
                    </p>
                  </>
                )}
              </Panel>
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel title="By source">
                <CountList rows={report.bySource} empty="No conversations in this period." />
              </Panel>
              <Panel title="By campaign">
                <CountList rows={report.byCampaign} empty="No conversations in this period." />
              </Panel>
            </div>
          </section>

          {/* ------------------------------------------------ tier 3 ------ */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">What came of it?</h2>

            {/* ⚠️⚠️ THE CREDIBILITY LINE, ABOVE the numbers rather than under
                them. A flattering conversion over a handful of classified
                conversations must not be readable as a result. */}
            {report.unclassifiedCount > 0 && (
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  <strong>
                    {report.unclassifiedCount} of {report.conversationsStarted} conversations have
                    no recorded outcome.
                  </strong>{' '}
                  Everything below is over the {report.classifiedCount} that do. Outcomes can't be
                  worked out later — they're set from the conversation itself.
                </p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <ReportStatCard
                label="Committed or joined"
                value={rateHeadline(report.conversionRate)}
                caption={rateCaption(report.conversionRate)}
              />
              <ReportStatCard
                label="Joined"
                value={rateHeadline(report.joinedRate)}
                caption={rateCaption(report.joinedRate)}
              />
              <ReportStatCard
                label="Classified"
                value={formatCount(report.classifiedCount)}
                caption={`of ${report.conversationsStarted} conversations`}
              />
              <ReportStatCard
                label="Not classified"
                value={formatCount(report.unclassifiedCount)}
                caption="we don't know what happened"
                tone="alert"
              />
            </div>

            <Panel title="Outcomes">
              {report.classifiedCount === 0 ? (
                // ⚠️ NOT a row of zeros. Nobody has classified anything, which
                // is a different fact from nobody booking anything.
                <p className="text-sm text-muted-foreground">
                  No outcomes recorded yet, so there is nothing to report here — {UNMEASURED}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {report.outcomes
                    .filter((row) => row.outcome !== 'unresolved' && row.count > 0)
                    .map((row) => (
                      <li key={row.outcome} className="flex items-center gap-2 text-sm">
                        <span className="w-10 shrink-0 text-right font-medium tabular-nums">
                          {row.count}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {LEAD_OUTCOME_LABELS[row.outcome]}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          {/* ⚠️ A dash, not "0%", when the sample is too small. */}
                          {row.share === null ? UNMEASURED : `${Math.round(row.share * 100)}%`}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </Panel>
          </section>

          {/* -------------------------------------------------- cost ------ */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold">What it costs to run</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <ReportStatCard
                label="Tokens per conversation"
                value={formatTokens(report.cost.tokensPerConversation)}
                caption={
                  report.cost.measuredConversations === 0
                    ? 'no token data in this period'
                    : `measured over ${report.cost.measuredConversations} of ${report.cost.totalConversations}`
                }
              />
              <ReportStatCard
                label="Tokens in / out"
                value={`${formatTokens(report.cost.inputTokens)} / ${formatTokens(report.cost.outputTokens)}`}
                caption={`${formatTokens(report.cost.cacheReadTokens)} read from cache`}
              />
              <ReportStatCard
                label="SMS segments"
                value={formatCount(report.cost.smsSegments)}
                caption="billable segments actually sent"
              />
              <ReportStatCard
                label="Segments per conversation"
                value={formatDecimal(report.cost.segmentsPerConversation, 1)}
              />
            </div>
            {/* ⚠️ Tokens, not dollars. A price baked in here would go stale with
                nothing to tell us, and a confident wrong dollar figure is worse
                than an honest token count. */}
            <p className="text-xs text-muted-foreground">
              Shown in tokens and segments rather than dollars — rates change, and a stale price
              presented to the cent would be worse than an honest count.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
